import { useEffect, useState } from "react";
import { FormModal } from "../../layout/Modal/Modal";
import { GiFertilizerBag } from "react-icons/gi";
import { FaTractor } from "react-icons/fa";
import IconButton from "../../common/IconButton";
import { TbSeeding } from "react-icons/tb";
import { FaRegSquareFull, FaTrailer, FaRegFilePdf } from "react-icons/fa6";
import { RiPlantLine } from "react-icons/ri";
import { MdGrass } from "react-icons/md";
import { ParcelData } from "../../../interfaces/ParcelData";
import { FieldType } from "../../../interfaces/FieldType";
import PlantProtectionForm from "../../tasks/PlantProtection/PlantProtectionForm";
import ParcelService from "../../../services/parcelService";
import { GoLock } from "react-icons/go";
import { CycleInfoPanel } from "../CycleInfoPanel";
import { ParcelCycle } from "../../../interfaces/ParcelCycle";
import HarvestForm from "../../tasks/Harvest/HarvestForm";
import UsageForm from "../../tasks/Usage/UsageForm";
import SeedForm from "../../tasks/Seed/SeedForm";
import TillageForm from "../../tasks/Tillage/TillageForm";
import FertilizerForm from "../../tasks/Fertilizer/FertilizerForm";
import ParcelForm from "../ParcelForm";

interface FormProps {
  parcel: ParcelData;
  cycle: ParcelCycle;
  showParcelForm?: boolean;
  onEditParcel?: () => void;
}

const TaskOverviewForm = (props: FormProps) => {
  const [showModal, setModal] = useState(false);
  const [showParcelForm] = useState<boolean>(props.showParcelForm ?? true);
  const [activeComponent, setActiveComponent] = useState<string | null>(null);
  const [currentParcel, setCurrentParcel] = useState<ParcelData>(props.parcel);
  const parcelService = new ParcelService();

  useEffect(() => {
    setCurrentParcel(props.parcel);
  }, [props.parcel]);

  const toggleModal = () => setModal((prev) => !prev);

  const switchComponent = (name: string) => {
    setActiveComponent(name);
    toggleModal();
  };

  const isForageProduction = () => currentParcel?.type === FieldType.Futterbau;

  const getParcelType = () =>
    isForageProduction() ? "Futterbau 🌾" : "Ackerbau 🌱";

  const onParcelEdit = async () => {
    try {
      const editedParcel = await parcelService.getParcelById(props.parcel.id!);
      setCurrentParcel(editedParcel);
    } catch (ignored: any) { }
    props.onEditParcel?.();
  };

  const exportPdf = async () => {
    try {
      const response = await parcelService.exportCycle(
        props.parcel!.id!,
        props.cycle.id!
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      const title = `${props.parcel.name}${props.parcel.subtype ? `-${props.parcel.subtype}` : ""
        }-${props.cycle.id}.pdf`;
      link.setAttribute("download", title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };

  return (
    <div>
      {/* Title */}
      <h1 className="text-lg font-bold text-gray-900 leading-tight">
        {currentParcel?.name}
      </h1>

      <div className="flex flex-col items-center justify-center text-center">
        <p className="text-l font-medium text-gray-700 leading-tight">
          Schlagkarte <b>{getParcelType()}</b>
          <br />
          {currentParcel?.subtype && (
            <>
              {isForageProduction() ? "Kultur: " : "Subtyp: "}
              <b>{props.parcel.subtype}</b>
            </>
          )}
        </p>
      </div>

      {/* Grid of Actions */}
      <div className="bg-white px-8 py-1 rounded-xl shadow-md min-w-[300px] grid grid-cols-2 sm:grid-cols-3 gap-5 mt-4 text-center">
        {showParcelForm && (
          <IconButton
            icon={<FaRegSquareFull size={24} />}
            label="Parzelle"
            onClick={() => switchComponent("parcel")}
          />
        )}
        <IconButton
          icon={<GiFertilizerBag size={24} />}
          label="Düngen"
          onClick={() => switchComponent("fertilizer")}
        />
        <IconButton
          icon={<FaTractor size={24} />}
          label="Bodenbearbeitung"
          onClick={() => switchComponent("tillage")}
          disabled={isForageProduction()}
        />
        <IconButton
          icon={<TbSeeding size={24} />}
          label="Saat"
          onClick={() => switchComponent("seed")}
        />
        <IconButton
          icon={<RiPlantLine size={24} />}
          label="Pflanzenschutz"
          onClick={() => switchComponent("plant-protection")}
        />
        <IconButton
          icon={<FaTrailer size={24} />}
          label="Ernte"
          onClick={() => switchComponent("harvest")}
          disabled={isForageProduction()}
        />
        <IconButton
          icon={<MdGrass size={24} />}
          label="Nutzung"
          onClick={() => switchComponent("usage")}
          disabled={!isForageProduction()}
        />
        <IconButton
          icon={<FaRegFilePdf size={24} />}
          label="Export"
          onClick={exportPdf}
        />
        <IconButton
          icon={<GoLock size={24} />}
          label="Abschluss"
          onClick={() => switchComponent("close")}
        />
      </div>

      {/* Modals */}
      {activeComponent === "parcel" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <ParcelForm
            onSave={toggleModal}
            parcel={currentParcel}
            onEdit={onParcelEdit}
          />
        </FormModal>
      )}

      {activeComponent === "fertilizer" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <FertilizerForm
            parcelId={Number(currentParcel.id)}
            cycleId={props.cycle.id!}
            cycleCompleted={props.cycle.completed}
          />
        </FormModal>
      )}

      {activeComponent === "tillage" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <TillageForm
            parcelId={Number(currentParcel.id)}
            cycleId={props.cycle.id!}
            cycleCompleted={props.cycle.completed}
          />
        </FormModal>
      )}

      {activeComponent === "harvest" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <HarvestForm
            parcelId={Number(currentParcel.id)}
            cycleId={props.cycle.id!}
            cycleCompleted={props.cycle.completed}
          />
        </FormModal>
      )}

      {activeComponent === "usage" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <UsageForm
            parcelId={Number(currentParcel.id)}
            cycleId={props.cycle.id!}
            cycleCompleted={props.cycle.completed}
          />
        </FormModal>
      )}

      {activeComponent === "seed" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <SeedForm
            parcelId={Number(currentParcel.id)}
            cycleId={props.cycle.id!}
            cycleCompleted={props.cycle.completed}
          />
        </FormModal>
      )}

      {activeComponent === "plant-protection" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <PlantProtectionForm
            parcelId={Number(currentParcel.id)}
            cycleId={props.cycle.id!}
            cycleCompleted={props.cycle.completed}
          />
        </FormModal>
      )}

      {activeComponent === "close" && (
        <FormModal open={showModal} onClose={toggleModal}>
          <CycleInfoPanel
            currentCycle={props.cycle}
            currentParcel={currentParcel} />
        </FormModal>
      )}
    </div>
  );
};

export default TaskOverviewForm;
